import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import type { TenantPermission } from '@loctopus/rbac';
import { api } from '../lib/api';
import { SETTINGS_MANAGE_PERMISSION, useAuth } from '../lib/auth';
import { MODULE_NOT_INCLUDED_MESSAGE, useClientPackage } from '../lib/clientPackage';
import type { ClientPackageModules } from '../lib/types';
import { ModeSwitch, SetupModeBanner, TestModeBanner } from './ModeSwitch';

interface NavItem {
  to: string;
  label: string;
  permission?: TenantPermission;
  /** docs/ARCHITECTURE_V2.md §3.4/§12 agent 7: when set and the tenant's package has this module
   * off, the item still renders (never silently hidden) but grayed out and non-navigable, labeled
   * with the owner's exact wording - a client must never confuse "not entitled" with "broken". */
  module?: keyof ClientPackageModules;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Catalog',
    items: [
      { to: '/events', label: 'Events', permission: 'events.manage' },
      {
        to: '/promo-codes',
        label: 'Promo codes',
        permission: 'promo_codes.manage',
        module: 'promoCodes',
      },
      { to: '/add-ons', label: 'Add-ons', permission: 'add_ons.manage', module: 'addOns' },
      {
        to: '/custom-questions',
        label: 'Custom questions',
        permission: 'custom_questions.manage',
        module: 'customQuestions',
      },
      {
        to: '/waitlist',
        label: 'Waitlist',
        permission: 'waitlists.manage',
        module: 'waitlists',
      },
    ],
  },
  {
    label: 'Sales',
    items: [
      { to: '/orders', label: 'Orders', permission: 'orders.view' },
      { to: '/reports', label: 'Reports', permission: 'reports.view', module: 'reports' },
    ],
  },
  {
    label: 'Money',
    items: [
      { to: '/payouts', label: 'Payouts', permission: 'payouts.view' },
      { to: '/invoices', label: 'Invoices', permission: 'invoices.view' },
      // No real tenant permission gates "view your own billing plan" today (confirmed by reading
      // packages/rbac/src/permissions.ts's TENANT_PERMISSIONS) - reusing `invoices.view`, the
      // closest existing "can see money/billing info" permission, rather than inventing one.
      // FLAG FOR RECONCILIATION if the backend agent adds a dedicated permission for this.
      { to: '/billing', label: 'Billing', permission: 'invoices.view' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { to: '/branding', label: 'Branding', permission: 'branding.manage' },
      { to: '/templates', label: 'Templates', permission: 'templates.manage' },
      { to: '/staff', label: 'Staff & roles', permission: 'staff.manage' },
      // ASSUMED permission - packages/rbac has no dedicated "edit refund policy" permission today
      // (confirmed by reading packages/rbac/src/permissions.ts). `settings.manage` follows this
      // nav list's own existing naming convention for the other `*.manage` tenant permissions
      // (branding.manage, templates.manage, staff.manage, ...) and is cast below since it isn't
      // part of the real `TenantPermission` union yet - see RefundPolicyPage.tsx's own doc
      // comment and this task's final report for the "Owner role only" gating this is meant to
      // satisfy (today, `staff.manage` is the only tenant permission exclusive to the Owner
      // preset among packages/rbac/src/presets.ts's TENANT_ROLE_PRESETS - `settings.manage` is
      // assumed to land the same way: granted to Owner by default, withheld from every other
      // preset, same as `staff.manage`/`roles.manage`/`api_keys.manage` already are).
      {
        to: '/refund-policy',
        label: 'Refund policy',
        permission: SETTINGS_MANAGE_PERMISSION,
      },
      {
        to: '/api-keys',
        label: 'API keys',
        permission: 'api_keys.manage',
        module: 'publicApiAccess',
      },
      { to: '/webhooks', label: 'Webhooks', permission: 'webhooks.manage' },
      { to: '/domains', label: 'Domains', permission: 'domains.manage' },
      { to: '/payment-accounts', label: 'Payment setup', permission: 'payment_accounts.manage' },
      // No `permission` - every signed-in staff member manages their OWN 2FA regardless of role.
      { to: '/security', label: 'Security' },
    ],
  },
];

export function DashboardLayout() {
  const { email, hasPermission, isLoading, refresh } = useAuth();
  const { hasModule, isLoading: pkgLoading } = useClientPackage();
  const navigate = useNavigate();

  async function handleLogout() {
    await api.auth.logout();
    await refresh();
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex min-h-screen bg-brand-bg">
      <aside className="w-60 shrink-0 border-r border-slate-200 bg-white flex flex-col">
        <div className="px-5 py-4 border-b border-slate-100">
          <p className="font-heading font-semibold text-brand-primary">Loctopus</p>
          <p className="text-xs text-slate-500">Client Dashboard</p>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {NAV_GROUPS.map((group) => {
            const visibleItems = group.items.filter(
              (item) => isLoading || !item.permission || hasPermission(item.permission),
            );
            if (visibleItems.length === 0) return null;
            return (
              <div key={group.label} className="mb-4">
                <p className="px-5 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {group.label}
                </p>
                {visibleItems.map((item) => {
                  // Fail open while the package is still loading (never flash "not included"
                  // before we actually know, same rule as lib/clientPackage.tsx's hasModule) -
                  // only gray out once we've positively confirmed the module is off.
                  const moduleOff = item.module && !pkgLoading && !hasModule(item.module);
                  if (moduleOff) {
                    return (
                      <span
                        key={item.to}
                        title={MODULE_NOT_INCLUDED_MESSAGE}
                        aria-disabled="true"
                        className="flex items-center justify-between gap-2 px-5 py-2 text-sm font-medium text-slate-400 cursor-not-allowed"
                      >
                        {item.label}
                        <span className="text-[10px] uppercase tracking-wide text-slate-400 border border-slate-200 rounded px-1 py-0.5">
                          Locked
                        </span>
                      </span>
                    );
                  }
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `block px-5 py-2 text-sm font-medium transition ${
                          isActive
                            ? 'bg-brand-accent/10 text-brand-primary border-r-2 border-brand-primary'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <ModeSwitch />
        <div className="px-5 py-3 border-t border-slate-100 text-xs text-slate-500 flex flex-col gap-1">
          <span>{email ?? 'Not signed in'}</span>
          <button type="button" onClick={handleLogout} className="text-left underline hover:text-slate-700">
            Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col overflow-y-auto">
        <SetupModeBanner />
        <TestModeBanner />
        <main className="flex-1 p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
